import React, { useState, useEffect } from 'react';
import { pageSectionService, mediaService } from '../../services/api';

function PageBuilderManagement() {
  const [sections, setSections] = useState([]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    page: 'home', section_type: 'content', title: '', content: '', image: '',
    button_text: '', button_link: '', bg_color: 'white', text_color: 'dark', sort_order: 0, is_active: true,
  });

  useEffect(() => { loadSections(); }, []);

  const templates = [
    { type: 'hero', name: 'Hero Banner', desc: 'Large headline with image/video style background' },
    { type: 'content', name: 'Content Block', desc: 'Title, rich text, and optional image' },
    { type: 'feature_grid', name: 'Feature Grid', desc: 'Professional cards for values, services, benefits' },
    { type: 'image_text', name: 'Image + Text', desc: 'Two-column corporate section' },
    { type: 'stats', name: 'Stats / Numbers', desc: 'Highlight company achievements' },
    { type: 'cta', name: 'Call To Action', desc: 'Dark professional conversion section' },
  ];

  const loadSections = async () => {
    try { setSections(await pageSectionService.index()); } catch (e) { console.error(e); }
  };

  const resetForm = (type = 'content') => {
    setEditingId(null);
    setForm({ page: selectedPage, section_type: type, title: '', content: '', image: '', button_text: '', button_link: '', bg_color: 'white', text_color: 'dark', sort_order: sections.length + 1, is_active: true });
  };

  const editSection = (section) => {
    setEditingId(section.id);
    setForm({ ...section });
    setSelectedPage(section.page);
  };

  const saveSection = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await pageSectionService.update(editingId, form);
      else await pageSectionService.store(form);
      resetForm(form.section_type);
      loadSections();
    } catch (e) { alert('Could not save section'); }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await pageSectionService.destroy(id);
    loadSections();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const media = await mediaService.upload(file, 'page-builder');
      setForm(prev => ({ ...prev, image: media.url }));
    } catch (e) { alert('Upload failed'); }
    setUploading(false);
  };

  const pageSections = sections.filter(s => s.page === selectedPage).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-900 to-red-900 text-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-3xl font-bold">Modern Page Builder</h2>
        <p className="text-slate-200 mt-2">Create professional dynamic pages using reusable sections, images, buttons, colors, and ordering.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <label className="block text-sm font-bold text-slate-700 mb-2">Select Page</label>
            <input value={selectedPage} onChange={e => { setSelectedPage(e.target.value); setForm(prev => ({ ...prev, page: e.target.value })); }} placeholder="home, about, investors" className="w-full border border-slate-300 rounded-lg p-3" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <h3 className="font-bold text-slate-900 mb-4">Section Templates</h3>
            <div className="space-y-3">
              {templates.map(t => (
                <button key={t.type} onClick={() => resetForm(t.type)} className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-red-300 hover:bg-red-50 transition">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={saveSection} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{editingId ? 'Edit Section' : 'Create Section'}</h3>
              <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold uppercase">{form.section_type}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input value={form.page} onChange={e => setForm({ ...form, page: e.target.value })} placeholder="Page Slug" className="border rounded-lg p-3" />
              <select value={form.section_type} onChange={e => setForm({ ...form, section_type: e.target.value })} className="border rounded-lg p-3">
                {templates.map(t => <option key={t.type} value={t.type}>{t.name}</option>)}
                <option value="pillars">Existing Pillars</option>
                <option value="portfolio">Existing Portfolio</option>
              </select>
            </div>

            <input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Section Title" className="w-full border rounded-lg p-3" />
            <textarea value={form.content || ''} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Content / HTML allowed" className="w-full border rounded-lg p-3" rows="5" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <input value={form.image || ''} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="Image URL" className="w-full border rounded-lg p-3" />
                <input type="file" accept="image/*" onChange={e => uploadImage(e.target.files[0])} className="text-sm" />
                {uploading && <div className="text-sm text-red-700">Uploading...</div>}
                {form.image && <img src={form.image} alt="Preview" className="h-36 w-full object-cover rounded-xl border" />}
              </div>
              <div className="space-y-3">
                <input value={form.button_text || ''} onChange={e => setForm({ ...form, button_text: e.target.value })} placeholder="Button Text" className="w-full border rounded-lg p-3" />
                <input value={form.button_link || ''} onChange={e => setForm({ ...form, button_link: e.target.value })} placeholder="Button Link" className="w-full border rounded-lg p-3" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.bg_color || 'white'} onChange={e => setForm({ ...form, bg_color: e.target.value })} className="border rounded-lg p-3">
                    <option value="white">White</option><option value="slate-50">Light Grey</option><option value="red-50">Light Red</option><option value="slate-900">Dark Corporate</option>
                  </select>
                  <select value={form.text_color || 'dark'} onChange={e => setForm({ ...form, text_color: e.target.value })} className="border rounded-lg p-3">
                    <option value="dark">Dark</option><option value="white">White</option><option value="red">Red</option>
                  </select>
                </div>
                <input type="number" value={form.sort_order || 0} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} placeholder="Sort Order" className="w-full border rounded-lg p-3" />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
              </div>
            </div>

            <div className="flex gap-3">
              <button className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700">{editingId ? 'Update Section' : 'Add Section'}</button>
              <button type="button" onClick={() => resetForm()} className="bg-slate-100 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-200">Reset</button>
            </div>
          </form>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Page Layout: {selectedPage}</h3>
            <div className="space-y-3">
              {pageSections.map(s => (
                <div key={s.id} className="flex items-center justify-between border border-slate-200 rounded-xl p-4 hover:bg-slate-50">
                  <div>
                    <div className="font-bold text-slate-900">{s.sort_order}. {s.title || 'Untitled Section'}</div>
                    <div className="text-xs text-slate-500 uppercase">{s.section_type} | {s.is_active ? 'Active' : 'Hidden'}</div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => editSection(s)} className="text-blue-600 font-semibold text-sm">Edit</button>
                    <button onClick={() => deleteSection(s.id)} className="text-red-600 font-semibold text-sm">Delete</button>
                  </div>
                </div>
              ))}
              {pageSections.length === 0 && <div className="text-center text-slate-500 py-8">No sections for this page yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageBuilderManagement;
